import React, { useState, useEffect, forwardRef } from 'react';
import { Input } from './input';
import { Label } from './label';
import { cn } from '../../lib/utils';
import { formatIndianNumber, parseIndianNumber, formatAsUserTypes, isValidNumber } from '../../lib/formatters';

export interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: number;
  onChange?: (value: number) => void;
  label?: string;
  error?: string;
  allowDecimals?: boolean;
  maxValue?: number;
  minValue?: number;
  unit?: string;
  formatLargeNumbers?: boolean;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ 
    value = 0, 
    onChange, 
    label, 
    error, 
    allowDecimals = true,
    maxValue,
    minValue = 0,
    unit,
    formatLargeNumbers = true,
    className,
    placeholder = "0",
    disabled,
    ...props 
  }, ref) => {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Update display value when prop value changes
    useEffect(() => {
      if (value || value === 0) {
        if (isFocused) {
          // Show raw number when focused for easier editing
          setDisplayValue(value.toString());
        } else {
          // Show formatted number when not focused
          if (formatLargeNumbers && value >= 1000) {
            setDisplayValue(formatIndianNumber(value, allowDecimals));
          } else {
            setDisplayValue(allowDecimals ? value.toFixed(2) : value.toString());
          }
        }
      } else {
        setDisplayValue('');
      }
    }, [value, isFocused, allowDecimals, formatLargeNumbers]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Convert to raw number for editing
      if (value || value === 0) {
        setDisplayValue(value.toString());
      }
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      
      // Validate and format on blur
      const numericValue = parseIndianNumber(displayValue);
      
      // Apply min/max constraints
      let constrainedValue = numericValue;
      if (minValue !== undefined && constrainedValue < minValue) {
        constrainedValue = minValue;
      }
      if (maxValue !== undefined && constrainedValue > maxValue) {
        constrainedValue = maxValue;
      }
      
      // Round to appropriate decimal places
      if (!allowDecimals) {
        constrainedValue = Math.round(constrainedValue);
      }
      
      // Update parent component
      onChange?.(constrainedValue);
      
      // Format display value
      if (formatLargeNumbers && constrainedValue >= 1000) {
        setDisplayValue(formatIndianNumber(constrainedValue, allowDecimals));
      } else {
        setDisplayValue(allowDecimals ? constrainedValue.toFixed(2) : constrainedValue.toString());
      }
      
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      if (isFocused) {
        // When focused, allow raw input but validate
        if (inputValue === '' || isValidNumber(inputValue)) {
          setDisplayValue(inputValue);
        }
      } else {
        // When not focused, format as user types
        if (formatLargeNumbers) {
          const formatted = formatAsUserTypes(inputValue);
          setDisplayValue(formatted);
          
          // Update parent with numeric value
          const numericValue = parseIndianNumber(formatted);
          onChange?.(numericValue);
        } else {
          setDisplayValue(inputValue);
          const numericValue = parseFloat(inputValue) || 0;
          onChange?.(numericValue);
        }
      }
    };

    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={props.id} className={error ? 'text-destructive' : ''}>
            {label}
            {props.required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        
        <div className="relative">
          <Input
            {...props}
            ref={ref}
            type="text"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              unit && 'pr-12',
              error && 'border-destructive focus-visible:ring-destructive',
              className
            )}
          />
          
          {unit && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none text-sm">
              {unit}
            </div>
          )}
        </div>
        
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        
        {!error && (maxValue || minValue !== undefined) && (
          <p className="text-xs text-muted-foreground">
            {minValue !== undefined && maxValue ? 
              `Range: ${formatIndianNumber(minValue, false)} - ${formatIndianNumber(maxValue, false)}${unit ? ` ${unit}` : ''}` :
              minValue !== undefined ? 
                `Minimum: ${formatIndianNumber(minValue, false)}${unit ? ` ${unit}` : ''}` :
                `Maximum: ${formatIndianNumber(maxValue!, false)}${unit ? ` ${unit}` : ''}`
            }
          </p>
        )}
      </div>
    );
  }
);

NumberInput.displayName = 'NumberInput';

export { NumberInput };